/**
 * useFocusFollowScroll · v10.26 (Feb 2026 · UAT A999682/A999683)
 * ────────────────────────────────────────────────────────────────
 * Global focus-follow behaviour for every text input in the app.
 *
 * Trevor's finding during the Client Intake walkthrough: when a form is
 * longer than the viewport, focusing a field further down doesn't scroll
 * it into view — the operator has to alt-scroll manually before typing.
 * Especially painful on the Client Intake and the Complete Site
 * Appraisal popouts.
 *
 * This hook installs ONE document-level focus listener. On any input /
 * textarea / [contenteditable] focus, we scroll it into the middle of
 * the viewport with a smooth animation. Idempotent — the listener is
 * added once at App mount and cleaned up if App ever unmounts.
 *
 * Guards:
 *  - Only fires on real user focus (event.isTrusted).
 *  - Skips elements marked `data-no-follow-scroll` for the rare case
 *    where a field IS supposed to stay in-place (e.g. a floating panel
 *    that would jitter if scrolled).
 *  - Skips when a modal / dialog is open (Radix keeps focus trapped and
 *    the modal manages its own scroll).
 *  - Debounced 60ms so rapid Tab-through doesn't fire multiple
 *    competing scroll animations.
 *
 * Zero per-form config. Every existing form benefits immediately.
 */
import { useEffect } from 'react';

const FOLLOWABLE = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export function useFocusFollowScroll() {
  useEffect(() => {
    let raf = null;
    const onFocus = (e) => {
      if (!e.isTrusted) return;
      const el = e.target;
      if (!el || typeof el.getBoundingClientRect !== 'function') return;
      // Only text-like inputs. Buttons + checkboxes don't need scroll-into-view.
      const tag = (el.tagName || '').toUpperCase();
      const isEditable = el.getAttribute && el.getAttribute('contenteditable') === 'true';
      if (!FOLLOWABLE.has(tag) && !isEditable) return;
      // Skip range/checkbox/radio/hidden/file — they don't benefit.
      const type = (el.type || '').toLowerCase();
      if (['checkbox', 'radio', 'range', 'hidden', 'file', 'button', 'submit'].includes(type)) return;
      // Explicit opt-out.
      if (el.closest && el.closest('[data-no-follow-scroll]')) return;
      // Modal / dialog trap — Radix + Shadcn own their own scroll.
      if (el.closest && el.closest('[role="dialog"], [role="alertdialog"]')) return;
      // rAF-debounce so rapid Tab-through doesn't spawn multiple scrolls.
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          // Guard: only scroll if the element is off-centre (avoids
          // unnecessary animation when it's already visible).
          const rect = el.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top < 80 || rect.bottom > vh - 80) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        } catch { /* jsdom / SSR — no-op */ }
      });
    };
    document.addEventListener('focusin', onFocus, true);
    return () => {
      document.removeEventListener('focusin', onFocus, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
}

export default useFocusFollowScroll;
